import { useCallback, useEffect, useState } from 'react';

import PublicPlatformQrCard from '../../public/PublicPlatformQrCard';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import { AcademicPanelCard } from './AcademicPanelCard';
import { getAdminSystemSettings, patchAdminSystemPlatform } from '../../../services/adminService';
import { displayPublicSiteHost } from '../../../utils/publicSiteOrigin';

export default function AcademiaPublicSitePanel() {
  const [publicSiteUrl, setPublicSiteUrl] = useState('');
  const [resolvedOrigin, setResolvedOrigin] = useState('');
  const [platformRegisterUrl, setPlatformRegisterUrl] = useState('');
  const [snapshotUrl, setSnapshotUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await getAdminSystemSettings();
      const p = data?.platform;
      const url = (p?.publicSiteUrl as string | null | undefined) ?? '';
      setPublicSiteUrl(url);
      setSnapshotUrl(url);
      setResolvedOrigin(String(p?.resolvedPublicSiteOrigin ?? ''));
      setPlatformRegisterUrl(String(p?.platformRegisterUrl ?? ''));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les paramètres.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(() => void load());

  const isDirty = publicSiteUrl !== snapshotUrl;
  const activeHost = displayPublicSiteHost(resolvedOrigin, publicSiteUrl || snapshotUrl);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await patchAdminSystemPlatform({
        publicSiteUrl: publicSiteUrl.trim() === '' ? null : publicSiteUrl.trim(),
      });
      const saved = (data?.publicSiteUrl as string | null | undefined) ?? '';
      setPublicSiteUrl(saved);
      setSnapshotUrl(saved);
      await load();
      setToast('URL publique enregistrée — les QR codes utilisent le nouveau domaine.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPageShell
      title="Site public & QR inscription"
      description="Indiquez l’adresse de votre site en ligne. Elle sera utilisée sur tous les QR codes d’inscription (page d’accueil, affiches, parrainage)."
      isLoading={loading}
      isError={Boolean(error) && !resolvedOrigin}
      errorMessage={error ?? undefined}
      onRetry={() => void load()}
      sideCard={{
        label: 'Domaine actif',
        value: loading
          ? '…'
          : resolvedOrigin
            ? (() => {
                try {
                  return new URL(resolvedOrigin).host;
                } catch {
                  return resolvedOrigin;
                }
              })()
            : '—',
        helper: platformRegisterUrl ? 'QR → création de compte' : undefined,
        sideLinks: [
          { label: 'Tableau de bord', to: '/admin/dashboard', icon: 'dashboard' },
          { label: 'Devise & taux', to: '/admin/settings', icon: 'settings' },
        ],
      }}
    >
      <AdminToast message={toast} variant="success" onClose={() => setToast(null)} />

      <div className="grid gap-6 lg:grid-cols-[1fr_minmax(280px,360px)]">
        <AcademicPanelCard
          icon={<LucideSymbolIcon name="public" tone="inherit" size={22} />}
          title="Adresse du site"
          subtitle="C’est l’adresse que les gens verront en scannant un QR code"
        >
          {error ? (
            <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
          ) : null}
          <label className="tarif-field block max-w-xl">
            <span className="tarif-field__label">Adresse du site (domaine)</span>
            <input
              type="text"
              className="tarif-field__input w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm"
              value={publicSiteUrl}
              onChange={(e) => setPublicSiteUrl(e.target.value)}
              placeholder="https://academia.jconsult.cd"
              disabled={loading || saving}
            />
            <span className="mt-1 block text-xs text-on-surface-variant">
              Exemple : <span className="font-mono">academia.jconsult.cd</span> ou{' '}
              <span className="font-mono">https://academia.jconsult.cd</span>. Utilisez l’adresse
              exacte de votre site une fois en ligne.
            </span>
          </label>

          <div className="mt-6 flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
              disabled={!isDirty || saving || loading}
              onClick={() => void save()}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button
              type="button"
              className="rounded-lg border border-outline-variant/30 px-4 py-2 text-sm font-bold text-slate-700 disabled:opacity-50"
              disabled={!isDirty || saving}
              onClick={() => setPublicSiteUrl(snapshotUrl)}
            >
              Annuler
            </button>
          </div>

          <p className="mt-4 flex items-start gap-2 rounded-lg bg-surface-container-low px-3 py-2 text-xs text-on-surface-variant">
            <LucideSymbolIcon name="info" tone="inherit" size={16} className="mt-0.5 shrink-0" />
            <span>
              Au scan du QR code, la personne ouvre directement la page « Créer un compte » sur
              l’adresse indiquée. Pratique pour les affiches, flyers et réseaux sociaux.
            </span>
          </p>
        </AcademicPanelCard>

        <AcademicPanelCard
          icon={<LucideSymbolIcon name="qr_code" tone="inherit" size={22} />}
          title="Aperçu du QR code"
          subtitle="Inscription sur le site, sans code parrain"
          variant="preview"
        >
          {platformRegisterUrl && activeHost ? (
            <PublicPlatformQrCard variant="admin" registerUrl={platformRegisterUrl} />
          ) : (
            <p className="text-sm text-on-surface-variant">
              {loading ? 'Chargement…' : 'Enregistrez l’adresse du site pour afficher le QR code.'}
            </p>
          )}
        </AcademicPanelCard>
      </div>
    </AdminPageShell>
  );
}
